import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toShortDate } from '@/lib/time';
import { Dropdown } from '../bootstrapJs';

import { CALENDAR_DEFAULT_MAX_DATE, CALENDAR_DEFAULT_MIN_DATE, NgCalendar } from './internal/NgCalendar';

export type NgInputDateProps = {
  value?: Date | null;
  onValueChanged?: (value: Date | null) => void | Promise<void>;
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  id?: string;
  cssClass?: string;
};

type DatePartOrder = ('day' | 'month' | 'year')[];

function datePartOrder(locale: string): DatePartOrder {
  const parts = new Intl.DateTimeFormat(locale).formatToParts(new Date(2000, 11, 31));
  return parts
    .filter((part): part is Intl.DateTimeFormatPart & { type: 'day' | 'month' | 'year' } =>
      ['day', 'month', 'year'].includes(part.type),
    )
    .map((part) => part.type);
}

function formatDate(date: Date | null | undefined, locale: string): string {
  return date != null ? toShortDate(date, locale) : '';
}

function parseDate(text: string, locale: string): Date | null | 'invalid' {
  const trimmed = text.trim();
  if (trimmed === '') {
    return null;
  }
  const numbers = trimmed
    .split(/[^0-9]+/)
    .filter((part) => part !== '')
    .map(Number);
  if (numbers.length !== 3 || numbers.some(Number.isNaN)) {
    return 'invalid';
  }
  const order = datePartOrder(locale);
  const values: Partial<Record<'day' | 'month' | 'year', number>> = {};
  order.forEach((part, index) => {
    values[part] = numbers[index];
  });
  const { day, month, year } = values;
  if (day == null || month == null || year == null) {
    return 'invalid';
  }
  const fullYear = year < 100 ? 2000 + year : year;
  const date = new Date(fullYear, month - 1, day);
  if (date.getFullYear() !== fullYear || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return 'invalid';
  }
  return date;
}

export function NgInputDate({
  value,
  onValueChanged,
  label,
  minDate,
  maxDate,
  id,
  cssClass,
}: NgInputDateProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en-US';

  const [generatedId] = useState(() => `el${crypto.randomUUID().replaceAll('-', '')}`);
  const inputId = id ?? generatedId;
  const [hintId] = useState(() => `el${crypto.randomUUID().replaceAll('-', '')}`);
  const inputRef = useRef<HTMLInputElement>(null);

  const [inputText, setInputText] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);

  const formattedValue = useMemo(() => formatDate(value, locale), [value, locale]);

  const hideDropdown = () => {
    const input = inputRef.current;
    if (input) {
      Dropdown.getOrCreateInstance(input).hide();
    }
  };

  const handleCalendarValueChanged = (date: Date) => {
    setInputText(null);
    setInvalid(false);
    hideDropdown();
    void onValueChanged?.(date);
  };

  const handleClearClick = () => {
    setInputText(null);
    setInvalid(false);
    hideDropdown();
    void onValueChanged?.(null);
  };

  const handleTodayClick = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    handleCalendarValueChanged(today);
  };

  const dateOnly = (date: Date): Date =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const isWithinBounds = (date: Date): boolean => {
    const day = dateOnly(date);
    if (minDate != null && day < dateOnly(minDate)) {
      return false;
    }
    if (maxDate != null && day > dateOnly(maxDate)) {
      return false;
    }
    return true;
  };

  const handleInputChange = (text: string) => {
    const parsed = parseDate(text, locale);
    if (parsed === 'invalid' || (parsed !== null && !isWithinBounds(parsed))) {
      setInputText(text);
      setInvalid(true);
      return;
    }
    setInputText(null);
    setInvalid(false);
    void onValueChanged?.(parsed);
  };

  return (
    <div className={`hx-form-group position-relative ${cssClass ?? ''}`}>
      {label != null && label !== '' && (
        <label htmlFor={inputId} className="form-label" onClick={(event) => event.stopPropagation()}>
          {label}
        </label>
      )}

      <div className="hx-input-date-wrapper hx-input-date-has-calendar-icon">
        <div className="dropdown hx-input-date">
          <input
            ref={inputRef}
            data-bs-toggle="dropdown"
            aria-expanded="false"
            data-bs-auto-close="outside"
            className={`form-control${invalid ? ' is-invalid' : ''}`}
            type="text"
            id={inputId}
            value={inputText ?? formattedValue}
            onChange={(event) => setInputText(event.target.value)}
            onBlur={(event) => handleInputChange(event.target.value)}
            onFocus={(event) => event.currentTarget.select()}
            inputMode="none"
          />

          <div className="dropdown-menu hx-input-date-dropdown-menu">
            <div className="hx-input-date-calendar">
              <NgCalendar
                value={value}
                onValueChanged={handleCalendarValueChanged}
                minDate={minDate ?? CALENDAR_DEFAULT_MIN_DATE}
                maxDate={maxDate ?? CALENDAR_DEFAULT_MAX_DATE}
              />
            </div>
            <div className="hx-input-date-dropdown-buttons">
              <button
                type="button"
                className="hx-button btn btn-link btn-sm"
                tabIndex={-1}
                onClick={handleClearClick}
              >
                Clear
              </button>
              <button
                type="button"
                className="hx-button btn btn-link btn-sm"
                tabIndex={-1}
                onClick={handleTodayClick}
              >
                Today
              </button>
            </div>
          </div>

          <div className="hx-input-date-icon">
            <i className="hx-icon bi-calendar"></i>
          </div>
        </div>
      </div>

      <div id={hintId} />
    </div>
  );
}
